import { INBOX_SOURCE_KINDS, type InboxSourceKind } from "./types"

/**
 * `<source>:<native id>` — the one spelling the unified inbox uses for anything
 * that belongs to exactly one source: item ids, and the `agent`, `channel` and
 * `status` filter values.
 *
 * The prefix is what lets the service leave the other two sources out of a page
 * *and say why*. A bare id would leave them queried and silently empty.
 */
export function scopedId(source: InboxSourceKind, nativeId: string | number | bigint): string {
  return `${source}:${nativeId}`
}

/** The source a scoped value belongs to, or null when it names none. */
export function sourceOfScopedValue(value: string): InboxSourceKind | null {
  const [prefix] = value.split(":", 1)
  return isSourceKind(prefix) ? prefix : null
}

/**
 * Everything after the first colon. Email ids carry one of their own
 * (`email:<inboxId>:<threadId>`), so only the first is the separator.
 */
export function idOfScopedValue(value: string): string {
  return value.slice(value.indexOf(":") + 1)
}

function isSourceKind(value: string): value is InboxSourceKind {
  return (INBOX_SOURCE_KINDS as readonly string[]).includes(value)
}

/**
 * `(inbox_id, thread_id)` — an email thread's own composite key, and the second
 * half of an `email:<inboxId>:<threadId>` item id. A Gmail thread id is unique
 * only within its mailbox, so neither half identifies a thread alone.
 */
export function threadKey(inboxId: string, threadId: string): string {
  return `${inboxId}:${threadId}`
}

/** The inverse. Null when the value is not a pair — an id from an editable URL. */
export function parseThreadKey(value: string): { inboxId: string; threadId: string } | null {
  const separator = value.indexOf(":")
  if (separator <= 0) return null
  const threadId = value.slice(separator + 1)
  return threadId ? { inboxId: value.slice(0, separator), threadId } : null
}
